import { Gamepad, KeyboardEventTypes, Vector3 } from '@babylonjs/core';
import { Button, Control, Grid, Image, Rectangle, TextBlock } from '@babylonjs/gui';
import { AssetFactory, ManifestEntry } from './AssetFactory';
import manifest from './assets/manifest.json';
import { Game } from './Game';
import { GameState } from './GameState';
import { Gui } from './gui/Gui';
import { LevelConfigLoader } from './LevelConfigLoader';

export class LevelSelection {
  private selectedIndex: number = 0;
  private inputTimer: number = 0;
  private levelButtons: Button[] = [];
  private cols: number = 4;
  private activeGamepad: Gamepad | null = null;
  private buttonAPressed: boolean = false;
  private isFirstFrame: boolean = true;

  constructor(public game: Game) {}

  async create() {
    await Gui.createDefaultScene(
      this.game,
      {
        url: 'assets/skybox/skybox_fantasy_castle.glb',
        rotation: new Vector3(-Math.PI / 2, 0, 0),
        rotationAxis: 'y',
      },
      'Select a Level'
    );
    await Gui.createBackButton(this.game, GameState.MAIN, '🔙');

    const levels: ManifestEntry[] = [];
    AssetFactory.findManifestFiles('.json', manifest as ManifestEntry, 'levels/', levels);
    const names = await LevelConfigLoader.getLevelNames();

    await this.game.sound.createBackgroundMusic('SongwriterBallad');

    this.game.setupGamepad((pad) => {
      this.activeGamepad = pad;
    });

    this.cols = Math.min(4, levels.length); // Dynamic columns up to 4
    const rows = Math.ceil(levels.length / this.cols);

    const g = this.game.gui2D.createGrid(
      'selection_grid',
      '95%',
      '65%',
      rows,
      this.cols,
      15,
      (grid: Grid, row: number, column: number, index: number) => {
        const level = levels[index];
        if (!level) return null as any;

        const key = level.name.replace('.json', '');
        const name = names[index];
        const unlocked = this.game.user.isUnLocked(index);
        const current = index === this.game.user.getMaxLevel() + 1;

        // Custom Button
        const button = new Button('btn_' + index);
        button.thickness = 3;
        button.color = 'transparent';
        button.cornerRadius = 15;

        const imgName = 'level' + (index + 1) + '.jpg';
        const img = new Image('img_' + index, 'assets/screens/' + imgName);
        img.stretch = Image.STRETCH_FILL;
        button.addControl(img);

        // Overlay Rectangle for readability
        const overlay = new Rectangle();
        overlay.background = 'rgba(0, 0, 0, 0.6)';
        overlay.thickness = 0;
        overlay.height = '35%';
        overlay.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        button.addControl(overlay);

        let iconText = '';
        if (!unlocked) {
          iconText = '🔒';
        } else if (current) {
          iconText = '🎮';
        } else {
          iconText = '✅';
        }

        let buttonText = name;

        if (unlocked) {
          const stat = this.game.user.getLevelStat(index);
          if (stat) {
            buttonText += `\n⭐️ ${stat.score}`;
          }
        }

        const textBlock = new TextBlock('text_' + index, buttonText);
        textBlock.color = 'white';
        textBlock.fontSize = '28%';
        textBlock.fontFamily = Gui.fontFamily;
        textBlock.fontWeight = 'bold';
        textBlock.textWrapping = true;
        textBlock.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;

        overlay.addControl(textBlock);

        const iconBlock = new TextBlock('icon_' + index, iconText);
        iconBlock.color = 'white';
        iconBlock.fontSize = '30%';
        iconBlock.fontFamily = Gui.fontFamily;
        iconBlock.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        iconBlock.top = '-15%';
        Gui.setDefaultShadow(iconBlock);

        button.addControl(iconBlock);

        if (!unlocked) {
          button.alpha = 0.5;
          img.alpha = 0.5;
        }

        button.onPointerUpObservable.add(() => {
          if (!unlocked) return;
          this.game.sound.play('click');
          this.game.levelKey = key;
          this.game.routeTo(GameState.LEVEL);
        });

        button.pointerEnterAnimation = () => {
          this.selectedIndex = index;
          this.updateSelection();
        };

        this.levelButtons[index] = button;
        return button;
      }
    );

    g.top = '15%';

    // Initial selection
    this.selectedIndex = this.game.user.getMaxLevel() + 1;
    if (this.selectedIndex >= levels.length) this.selectedIndex = levels.length - 1;
    this.updateSelection();

    this.game.removeableObservers.push(
      this.game.scene.onKeyboardObservable.add((kbInfo) => {
        if (this.game.gameState !== GameState.SELECTION) return;
        if (kbInfo.type === KeyboardEventTypes.KEYDOWN) {
          const now = Date.now();
          if (now - this.inputTimer > 200) {
            let moved = false;
            if (kbInfo.event.code === 'ArrowRight') {
              if ((this.selectedIndex + 1) % this.cols !== 0 && this.selectedIndex + 1 < levels.length) {
                this.selectedIndex++;
                moved = true;
              }
            } else if (kbInfo.event.code === 'ArrowLeft') {
              if (this.selectedIndex % this.cols !== 0) {
                this.selectedIndex--;
                moved = true;
              }
            } else if (kbInfo.event.code === 'ArrowUp') {
              if (this.selectedIndex - this.cols >= 0) {
                this.selectedIndex -= this.cols;
                moved = true;
              }
            } else if (kbInfo.event.code === 'ArrowDown') {
              if (this.selectedIndex + this.cols < levels.length) {
                this.selectedIndex += this.cols;
                moved = true;
              }
            }

            if (moved) {
              this.game.sound.play('click');
              this.updateSelection();
              this.inputTimer = now;
            }
          }
        } else if (kbInfo.type === KeyboardEventTypes.KEYUP) {
          if (kbInfo.event.code === 'Space' || kbInfo.event.code === 'Enter') {
            this.confirmSelection(levels);
          }
        }
      })
    );

    const observer = this.game.scene.onBeforeRenderObservable.add(() => this.updateInput(levels));
    this.game.removeableObservers.push(observer);
  }

  updateSelection() {
    this.levelButtons.forEach((btn, idx) => {
      if (!btn) return;
      if (idx === this.selectedIndex) {
        btn.color = 'white';
        btn.thickness = 5;
        btn.scaleX = 1.05;
        btn.scaleY = 1.05;
      } else {
        btn.color = 'transparent';
        btn.thickness = 3;
        btn.scaleX = 1.0;
        btn.scaleY = 1.0;
      }
    });
  }

  updateInput(levels: ManifestEntry[]) {
    if (this.game.gameState !== GameState.SELECTION) return;

    const pad = this.activeGamepad?.browserGamepad;
    if (!pad) return;

    let x = pad.axes[0] || 0;
    let y = pad.axes[1] || 0;

    if (pad.buttons[14]?.pressed) x = -1; // d-pad left
    if (pad.buttons[15]?.pressed) x = 1; // d-pad right
    if (pad.buttons[12]?.pressed) y = -1; // d-pad up
    if (pad.buttons[13]?.pressed) y = 1; // d-pad down

    const now = Date.now();
    if (now - this.inputTimer > 200) {
      let moved = false;

      if (x > 0.5) {
        if ((this.selectedIndex + 1) % this.cols !== 0 && this.selectedIndex + 1 < levels.length) {
          this.selectedIndex++;
          moved = true;
        }
      } else if (x < -0.5) {
        if (this.selectedIndex % this.cols !== 0) {
          this.selectedIndex--;
          moved = true;
        }
      } else if (y < -0.5) {
        if (this.selectedIndex - this.cols >= 0) {
          this.selectedIndex -= this.cols;
          moved = true;
        }
      } else if (y > 0.5) {
        if (this.selectedIndex + this.cols < levels.length) {
          this.selectedIndex += this.cols;
          moved = true;
        }
      }

      if (moved) {
        this.game.sound.play('click');
        this.updateSelection();
        this.inputTimer = now;
      }
    }

    const aPressed = pad.buttons[0]?.pressed;
    if (this.isFirstFrame) {
      this.buttonAPressed = aPressed || false;
      this.isFirstFrame = false;
    } else {
      if (aPressed && !this.buttonAPressed) {
        this.confirmSelection(levels);
      }
      this.buttonAPressed = aPressed || false;
    }
  }

  confirmSelection(levels: ManifestEntry[]) {
    const unlocked = this.game.user.isUnLocked(this.selectedIndex);
    if (unlocked) {
      this.game.sound.play('click');
      const level = levels[this.selectedIndex];
      this.game.levelKey = level.name.replace('.json', '');
      this.game.routeTo(GameState.LEVEL);
    }
  }
}
